import React, { useEffect, useMemo, useRef } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Field, Flex, Stack, Text } from '@chakra-ui/react'

import {
  Input,
  Label,
  PrimaryButton,
  RadioButton,
  Term,
  TertiaryButton,
  toaster,
} from '@enechain/ecloud-designsystem'

import { ProfilePicture } from '~/components/parts/ProfilePicture'
import { Language } from '~/gen/proto/bff/v1/model_pb'
import { useLoginUser } from '~/hooks/useLoginUser'
import { getLngString } from '~/i18n/i18nProvider'
import {
  ProfileEditSchema,
  profileEditSchema,
} from '~/pages/Profile/components/ProfileEditForm/profileEditSchema'
import { useProfileMutation } from '~/pages/Profile/hooks/useProfileMutation'
import { useUploadProfileImage } from '~/pages/Profile/hooks/useUploadProfileImage'
import { generateUint8Array } from '~/utils/img'
import {
  validateEmptyNumber,
  validateNameLength,
} from '~/validation/phonenumber'

export const ProfileEditForm: React.FC = () => {
  const { t: _t } = useTranslation('common', {
    keyPrefix: 'pages.members',
  })
  const label = useMemo(() => {
    return {
      lng: `${_t('label.language', { lng: 'ja' })} / ${_t('label.language', { lng: 'en' })}`,
      jp: `${_t('value.japanese', { lng: 'ja' })} / ${_t('value.japanese', { lng: 'en' })}`,
      en: `${_t('value.english', { lng: 'ja' })} / ${_t('value.english', { lng: 'en' })}`,
    }
  }, [_t])
  const { t } = useTranslation()
  const navigate = useNavigate()

  const me = useLoginUser()
  const { mutate: saveProfile, isPending: isSavingProfile } =
    useProfileMutation()
  const { mutate: uploadProfileImage, isPending: isUploadingImage } =
    useUploadProfileImage()

  const schema = profileEditSchema(t)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<ProfileEditSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: me.language,
    },
  })

  const onSubmit = (data: ProfileEditSchema): void => {
    if (
      !validateEmptyNumber(
        {
          phoneNumber: { old: me.phoneNumber, newVal: data.phoneNumber },
          mobileNumber: { old: me.mobileNumber, newVal: data.mobileNumber },
        },
        setError,
      )
    ) {
      return
    }
    if (!validateNameLength(data.name, setError)) {
      return
    }
    saveProfile(data)
    toaster.create({
      title: t('message.success_change', {
        entity: t('domain.profile', { lng: getLngString(data.language) }),
        lng: getLngString(data.language),
      }),
      type: 'success',
    })
  }

  const onResetPassword = (): void => {
    window.location.href = import.meta.env.VITE_ELOGIN_URL + '/change-password'
  }
  const params = useSearch({ from: '/_authenticated/profile' })
  const ref = useRef(true)

  const uploadImageRef = useRef<HTMLInputElement>(null)

  const handleClickInputFile = (): void => {
    const input = uploadImageRef?.current
    if (input != null) {
      input.click()
    }
  }

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files != null) {
      const img = event.target.files[0]
      generateUint8Array(img)
        .then((img) => {
          uploadProfileImage({
            image: img,
          })
        })
        .catch(() => {
          throw new Error(t('pages.profile.action.uploadImage.error'))
        })
    }
  }

  useEffect(() => {
    if (Object.hasOwn(params, 'success') && ref.current) {
      ref.current = false
      toaster.create({
        type: 'success',
        title: t('message.success_change', {
          entity: t('pages.members.label.password'),
        }),
      })
      navigate({ replace: true }).catch((e) => console.error(e))
    }
  }, [params['success']])

  return (
    <Stack gap="6" width="100%">
      <Flex alignItems="center" gap="2">
        <ProfilePicture
          // アップロードしてもファイル名は変わらないので、画像が切り替わらない。再取得できるようにクエリパラメータを付与する
          src={me.imageUrl + '?' + new Date().toISOString()}
        />
        <Input
          ref={uploadImageRef}
          aria-label="file-uploader"
          hidden
          onChange={handleFileChange}
          type="file"
        />
        <TertiaryButton onClick={handleClickInputFile} size="sm">
          {isUploadingImage
            ? t('pages.profile.action.uploadImage.isUploading')
            : t('pages.profile.action.uploadImage.button')}
        </TertiaryButton>
      </Flex>
      <Flex
        padding="6"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="radiiMd"
      >
        <Flex flexDirection="column" gap="6" width="50%">
          <Term>
            <Label required>{t('pages.members.label.name')}</Label>
            <Input
              defaultValue={me.name}
              {...register('name')}
              error={errors.name?.message}
            />
            <Text textStyle="textSm" color="Text.midEmphasis">
              {t('pages.members.description.name')}
            </Text>
          </Term>
          <Term>
            <Label>{t('pages.members.label.englishName')}</Label>
            <Input
              defaultValue={me.englishName}
              {...register('englishName')}
              error={errors.englishName?.message}
            />
            <Text textStyle="textSm" color="Text.midEmphasis">
              {t('pages.members.description.englishName')}
            </Text>
          </Term>

          <Term>
            <Label required>{t('pages.members.label.email')}</Label>
            <Text width="200%">{me.email}</Text>
            <Text
              textStyle="textSm"
              width="200%"
              color="Text.lowEmphasis"
              whiteSpace="prewrap"
            >
              {t('pages.profile.action.email_readonly.description')}
            </Text>
          </Term>

          <Term>
            <Label>{t('pages.members.label.password')}</Label>
            <TertiaryButton
              width="300px"
              maxWidth="200%"
              whiteSpace="prewrap"
              onClick={onResetPassword}
            >
              {t('action.change_something', {
                entity: t('pages.members.label.password'),
              })}
            </TertiaryButton>
          </Term>

          <Field.Root>
            <Controller
              control={control}
              name="language"
              render={({ field: { onChange, value } }): React.ReactElement => (
                <Term>
                  <Label required>{label.lng}</Label>
                  <RadioButton
                    defaultValue={me.language.toString()}
                    onValueChange={({ value }): void => {
                      if (value !== null) {
                        onChange(value)
                      }
                    }}
                    radios={[
                      {
                        label: label.jp,
                        value: Language.JAPANESE.toString(),
                      },
                      {
                        label: label.en,
                        value: Language.ENGLISH.toString(),
                      },
                    ]}
                    value={value.toString()}
                  />
                </Term>
              )}
            />
          </Field.Root>

          <Term>
            <Label>{t('pages.members.label.phoneNumber')}</Label>
            <Input
              defaultValue={me.phoneNumber}
              {...register('phoneNumber')}
              error={errors.phoneNumber?.message}
            />
          </Term>

          <Term>
            <Label>{t('pages.members.label.mobileNumber')}</Label>
            <Input
              defaultValue={me.mobileNumber}
              {...register('mobileNumber')}
              error={errors.mobileNumber?.message}
            />
          </Term>
        </Flex>
      </Flex>

      <Flex justifyContent="right" gap="2" width="100%">
        <PrimaryButton
          loading={isSavingProfile}
          onClick={handleSubmit(onSubmit)}
        >
          {t('action.save')}
        </PrimaryButton>
      </Flex>
    </Stack>
  )
}
